import React from 'react';
import { IconButton, Tooltip, Menu, MenuItem } from '@mui/material';
import { PictureAsPdf, GridOn, TableChart, Download } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';

const DataExporter = ({
    columns = [],
    data = [],
    fileName = 'exported-data',
    excludeColumns = [],
}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const getFilteredColumns = () => {
        return excludeColumns.length > 0
            ? columns.filter(col => !excludeColumns.includes(col.key))
            : columns;
    };

    const capitalizeFileName = (name) => {
        return name
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const checkDataBeforeExport = () => {
        if (!data || data.length === 0) {
            toast.error('No data available to export');
            return false;
        }
        return true;
    };

    // Helper function to extract text from React elements
    const extractTextFromReactElement = (element) => {
        if (typeof element === 'string' || typeof element === 'number') {
            return element.toString();
        }
        
        if (React.isValidElement(element)) {
            // Check for props.children
            if (element.props && element.props.children) {
                if (Array.isArray(element.props.children)) {
                    return element.props.children
                        .map(child => extractTextFromReactElement(child))
                        .join('');
                } else {
                    return extractTextFromReactElement(element.props.children);
                }
            }
            
            // Check for other props that might contain text
            if (element.props && element.props.title) {
                return element.props.title;
            }
            
            if (element.props && element.props.label) {
                return element.props.label;
            }
            
            if (element.props && element.props.value) {
                return element.props.value;
            }
            
            return ''; // Return empty string if no text can be extracted
        }
        
        return element !== undefined && element !== null ? element.toString() : '';
    };

    const prepareData = async () => {
        setIsLoading(true);

        try {
            if (!checkDataBeforeExport()) return null;

            const exportData = data;
            const filteredColumns = getFilteredColumns();

            // Prepare headers
            const headers = ['S.No.', ...filteredColumns.map(col => col.label || col.key)];

            // Prepare rows with serial numbers
            const rows = exportData.map((row, index) => [
                index + 1,
                ...filteredColumns.map(col => {
                    // Handle rendered values
                    if (col.render) {
                        const renderedValue = col.render(row, index);
                        return extractTextFromReactElement(renderedValue);
                    }
                    return row[col.key] !== undefined && row[col.key] !== null ? row[col.key] : '';
                })
            ]);

            return { headers, rows };
        } finally {
            setIsLoading(false);
        }
    };

    const downloadCSV = async () => {
        const preparedData = await prepareData();
        if (!preparedData) return;

        try {
            const { headers, rows } = preparedData;
            const capitalizedFileName = capitalizeFileName(fileName);

            // Convert data to CSV format
            let csvContent = headers.join(',') + '\n';
            rows.forEach(row => {
                csvContent += row.map(field => {
                    // Convert to string and handle special characters
                    const stringValue = String(field).replace(/"/g, '""');
                    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                        return `"${stringValue}"`;
                    }
                    return stringValue;
                }).join(',') + '\n';
            });

            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, `${capitalizedFileName}.csv`);
            toast.success('CSV exported successfully');
        } catch (error) {
            console.error('CSV export error:', error);
            toast.error('Failed to export CSV');
        } finally {
            handleMenuClose();
        }
    };

    const downloadExcel = async () => {
        const preparedData = await prepareData();
        if (!preparedData) return;

        try {
            const { headers, rows } = preparedData;
            const capitalizedFileName = capitalizeFileName(fileName);

            // Create workbook
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

            // Set column widths based on content
            const colWidths = headers.map((header, i) => {
                const maxContentLength = Math.max(
                    header.toString().length,
                    ...rows.map(row => (row[i] ? row[i].toString().length : 0))
                );
                return { wch: Math.min(Math.max(maxContentLength, 10), 50) };
            });
            ws['!cols'] = colWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

            // Write and download file
            XLSX.writeFile(wb, `${capitalizedFileName}.xlsx`);
            toast.success('Excel exported successfully');
        } catch (error) {
            console.error('Excel export error:', error);
            toast.error('Failed to export Excel');
        } finally {
            handleMenuClose();
        }
    };

    const downloadPDF = async () => {
        const preparedData = await prepareData();
        if (!preparedData) return;

        try {
            const { headers, rows } = preparedData;
            const capitalizedFileName = capitalizeFileName(fileName);
            const doc = new jsPDF();

            // Add title and date
            doc.setFontSize(16);
            doc.text(capitalizedFileName, 14, 15);
            doc.setFontSize(10);
            doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);

            autoTable(doc, {
                head: [headers],
                body: rows,
                startY: 30,
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    halign: 'left',
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: '#3498db',
                    textColor: '#FFFFFF',
                    fontStyle: 'bold',
                    halign: 'left',
                },
                columnStyles: {
                    0: { halign: 'center', cellWidth: 'auto' },
                },
                margin: { top: 30 },
                theme: 'grid'
            });

            doc.save(`${capitalizedFileName}.pdf`);
            toast.success('PDF exported successfully');
        } catch (error) {
            console.error('PDF export error:', error);
            toast.error('Failed to export PDF');
        } finally {
            handleMenuClose();
        }
    };

    return (
        <>
            <Tooltip title="Export data">
                <span>
                    <IconButton
                        onClick={handleMenuOpen}
                        size="small"
                        aria-label="export data"
                        disabled={isLoading}
                    >
                        <Download color="primary" />
                    </IconButton>
                </span>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={downloadCSV} disabled={isLoading}>
                    <TableChart fontSize="small" sx={{ mr: 1 }} /> Download CSV
                </MenuItem>
                <MenuItem onClick={downloadExcel} disabled={isLoading}>
                    <GridOn fontSize="small" sx={{ mr: 1 }} /> Download Excel
                </MenuItem>
                <MenuItem onClick={downloadPDF} disabled={isLoading}>
                    <PictureAsPdf fontSize="small" sx={{ mr: 1 }} /> Download PDF
                </MenuItem>
            </Menu>
        </>
    );
};

export default DataExporter;